/**
 * File: Includes/Modules/Common/Assets/home-feed.js
 * Description: Client-side controller for the Consolidated Home Feed Widget.
 * Handles tab switches, AJAX queries, pagination, signature loader, and end-of-list state.
 */

(function () {
  'use strict';

  const GRID_SEL = '#bfwt-home-feed-grid';
  const TAB_SEL = '.bfwt-home-feed__tab';
  const LOAD_MORE_BTN = '.bfwt-home-feed__load-more-btn';
  const FOOTER_SEL = '.bfwt-home-feed__footer';
  const END_OF_LIST_SEL = '.bfwt-home-feed__end-of-list';
  const EMPTY_SEL = '.bfwt-home-feed__empty';

  const SKELETON_COUNT = 8;
  const SKELETONS_HTML = `
    <div class="bfwt-home-feed__skeleton">
      <div class="bfwt-home-feed__skeleton-media"></div>
      <div class="bfwt-home-feed__skeleton-body">
        <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--title"></div>
        <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--discount"></div>
        <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--intro"></div>
        <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--footer"></div>
      </div>
    </div>
  `.repeat(SKELETON_COUNT);

  class HomeFeedController {
    constructor(container) {
      this.container = container;
      this.grid = container.querySelector(GRID_SEL);
      this.loadMoreBtn = container.querySelector(LOAD_MORE_BTN);
      this.defaultBtnHtml = this.loadMoreBtn ? this.loadMoreBtn.innerHTML : '<span>Load More</span>';
      this.footer = container.querySelector(FOOTER_SEL);
      this.endOfList = container.querySelector(END_OF_LIST_SEL);
      this.emptyState = container.querySelector(EMPTY_SEL);

      const activeTab = container.querySelector(
        '.bfwt-home-feed__tab.is-active'
      );
      this.feedType = activeTab ? activeTab.dataset.feedType : 'collection';
      this.termId = activeTab ? parseInt(activeTab.dataset.termId, 10) : 0;
      this.currentPage = 1;
      this.isLoading = false;

      this.ajaxUrl = window.ajaxurl || '/wp-admin/admin-ajax.php';

      this.bindEvents();
    }

    bindEvents() {
      // Feed Tab Clicks
      this.container.querySelectorAll(TAB_SEL).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.isLoading || btn.classList.contains('is-active')) return;
          this.switchTab(btn);
        });
      });

      // Load More Clicks
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.isLoading) return;
          this.loadMore();
        });
      }
    }

    switchTab(tabEl) {
      // Set Active Classes
      this.container
        .querySelectorAll(TAB_SEL)
        .forEach((el) => el.classList.remove('is-active'));
      tabEl.classList.add('is-active');
      if (typeof tabEl.scrollIntoView === 'function') {
        tabEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      // Update State
      this.feedType = tabEl.dataset.feedType;
      this.termId = parseInt(tabEl.dataset.termId, 10);
      this.currentPage = 1;

      // Reset feed view
      this.grid.innerHTML = SKELETONS_HTML;
      this.emptyState.classList.add('tw-hidden');
      this.footer.classList.add('tw-hidden');
      if (this.endOfList) this.endOfList.classList.add('tw-hidden');

      this.fetchBrands(false);
    }

    loadMore() {
      this.currentPage++;
      this.loadMoreBtn.disabled = true;
      this.loadMoreBtn.classList.add('is-loading');
      if (window.BFWT_SignatureLoaderHtml) {
        this.loadMoreBtn.innerHTML = window.BFWT_SignatureLoaderHtml('Loading more', true);
      }
      this.fetchBrands(true);
    }

    restoreLoadMoreBtn() {
      if (!this.loadMoreBtn) return;
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.classList.remove('is-loading');
      this.loadMoreBtn.innerHTML = this.defaultBtnHtml;
    }

    fetchBrands(append = false) {
      this.isLoading = true;
      if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.show();

      const url = `${this.ajaxUrl}?action=bfwt_load_home_feed_brands&feed_type=${this.feedType}&term_id=${this.termId}&paged=${this.currentPage}`;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error('Network error loading home feed.');
          return res.json();
        })
        .then((res) => {
          if (!res.success) {
            throw new Error(res.data || 'Error response from server.');
          }

          const data = res.data;

          if (append) {
            // Append Mode
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = data.html;

            // Collect all grid items
            const newItems = tempDiv.querySelectorAll('.bfwt-grid-item');
            newItems.forEach((item) => {
              item.style.opacity = '0';
              this.grid.appendChild(item);
              // Fade in card smoothly
              setTimeout(() => {
                item.style.transition = 'opacity 0.25s ease-in-out';
                item.style.opacity = '1';
              }, 50);
            });

            this.restoreLoadMoreBtn();
          } else {
            // Overwrite Mode (Tab Switch)
            this.grid.innerHTML = data.html;

            if (!data.html || data.html.trim() === '') {
              this.emptyState.classList.remove('tw-hidden');
            } else {
              this.emptyState.classList.add('tw-hidden');
            }
          }

          const hasRenderedCards = Boolean(this.grid.querySelector('.bfwt-grid-item'));

          // Toggle Load More & End of List Footers
          if (data.has_more) {
            this.footer.classList.remove('tw-hidden');
            if (this.endOfList) this.endOfList.classList.add('tw-hidden');
          } else {
            this.footer.classList.add('tw-hidden');
            if (this.endOfList) {
              if (hasRenderedCards) {
                this.endOfList.classList.remove('tw-hidden');
              } else {
                this.endOfList.classList.add('tw-hidden');
              }
            }
          }

          this.isLoading = false;
          if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.hide(260);
        })
        .catch((err) => {
          console.error(err);
          this.isLoading = false;
          if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.hide(120);
          this.restoreLoadMoreBtn();

          // Display error or fall back to empty
          if (!append) {
            this.grid.innerHTML = '';
            this.emptyState.classList.remove('tw-hidden');
            if (this.endOfList) this.endOfList.classList.add('tw-hidden');
          }
        });
    }
  }

  // Auto-initialize when DOM ready
  document.addEventListener('DOMContentLoaded', () => {
    const feedWidget = document.querySelector('.bfwt-home-feed');
    if (feedWidget) {
      new HomeFeedController(feedWidget);
    }
  });
})();
